/**
 * OpenTelemetry instrumentation for the React/Next.js frontend (RUM).
 * 
 * This module initializes OpenTelemetry tracing, metrics, and logs in the browser
 * following the Elastic RUM documentation pattern.
 * 
 * Traces, metrics, and logs are sent directly to the OpenTelemetry Collector
 * which forwards them to Elastic APM Server.
 * 
 * Reference: https://www.elastic.co/docs/solutions/observability/applications/otel-rum
 */

'use client';

import { diag, DiagConsoleLogger } from '@opentelemetry/api';
import { diagLogLevelFromString, SDK_INFO } from '@opentelemetry/core';
import { resourceFromAttributes, detectResources } from '@opentelemetry/resources';
import { browserDetector } from '@opentelemetry/opentelemetry-browser-detector';
import { 
  SEMRESATTRS_SERVICE_NAME, 
  SEMRESATTRS_SERVICE_VERSION, 
  SEMRESATTRS_DEPLOYMENT_ENVIRONMENT 
} from '@opentelemetry/semantic-conventions';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor, SpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { metrics } from '@opentelemetry/api';
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import { logs, SeverityNumber } from '@opentelemetry/api-logs';
import { BatchLogRecordProcessor, LoggerProvider } from '@opentelemetry/sdk-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { LongTaskInstrumentation } from '@opentelemetry/instrumentation-long-task';
import { W3CTraceContextPropagator, CompositePropagator } from '@opentelemetry/core';
import { propagation } from '@opentelemetry/api';

let tracerProvider: WebTracerProvider | null = null;
let meterProvider: MeterProvider | null = null;
let loggerProvider: LoggerProvider | null = null;
let isInitialized = false;

function resolveOtlpSignalUrl(
  signalEndpoint: string | undefined,
  baseEndpoint: string | undefined,
  signalPath: '/v1/traces' | '/v1/metrics' | '/v1/logs'
): string {
  if (signalEndpoint) {
    return signalEndpoint;
  }

  const normalizedBase = (baseEndpoint || 'http://localhost:4318').replace(/\/$/, '');
  return `${normalizedBase}${signalPath}`;
}

function safeUrlPath(input: string): string | null {
  try {
    const url = new URL(input, window.location.origin);
    return url.pathname || '/';
  } catch {
    return null;
  }
}

function normalizeHttpSpanName(method: string | undefined, url: string | undefined): string | null {
  if (!url) return null;
  const path = safeUrlPath(url);
  if (!path) return null;
  const verb = (method || 'GET').toUpperCase();
  return `${verb} ${path}`;
}

function getSpanAttribute(span: unknown, key: string): string | undefined {
  const attrs = (span as { attributes?: Record<string, unknown> }).attributes;
  const value = attrs?.[key];
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return undefined;
}

function normalizeHttpSpanNameFromSpan(span: unknown): string | null {
  const method =
    getSpanAttribute(span, 'http.method') ||
    getSpanAttribute(span, 'http.request.method') ||
    getSpanAttribute(span, 'http.request.method.original');

  const url =
    getSpanAttribute(span, 'url.full') ||
    getSpanAttribute(span, 'http.url') ||
    getSpanAttribute(span, 'http.target');

  return normalizeHttpSpanName(method, url);
}

class HttpSpanNameProcessor implements SpanProcessor {
  onStart(span: unknown): void {
    const name = normalizeHttpSpanNameFromSpan(span);
    if (!name) return;
    (span as { updateName?: (n: string) => void }).updateName?.(name);
    const path = name.split(' ').slice(1).join(' ');
    (span as { setAttribute?: (k: string, v: string) => void }).setAttribute?.(
      'http.route',
      path
    );
  }

  onEnd(): void {}

  shutdown(): Promise<void> {
    return Promise.resolve();
  }

  forceFlush(): Promise<void> {
    return Promise.resolve();
  }
}

const ignoredRequestUrls: Array<RegExp> = [
  /\/_next\//,
  /\/favicon\.ico$/,
  /\/robots\.txt$/,
  /\/sitemap\.xml$/,
  /\/sockjs-node/,
  /\.(css|js|map|png|jpe?g|gif|svg|webp|ico|woff2?|ttf)(\?|$)/i,
];

/**
 * Initialize OpenTelemetry tracing and metrics for the browser.
 * 
 * This should be called once when the app loads, typically in a client component.
 * Follows the official OpenTelemetry web SDK initialization pattern.
 * 
 * @param serviceName - Name of the service (default: 'ui')
 * @param serviceVersion - Version of the service (default: '1.0.0')
 * @param config - Optional configuration overrides
 */
export function initializeOpenTelemetry(
  serviceName: string = 'ui',
  serviceVersion: string = '1.0.0',
  config?: {
    tracesEndpoint?: string;
    metricsEndpoint?: string;
    logsEndpoint?: string;
  }
): { tracerProvider: WebTracerProvider | null; meterProvider: MeterProvider | null; loggerProvider: LoggerProvider | null } {
  // Only initialize once
  if (isInitialized) {
    return { tracerProvider, meterProvider, loggerProvider };
  }

  // Skip initialization in development if disabled
  if (process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_OTEL_DISABLED === 'true') {
    console.log('OpenTelemetry disabled in development');
    return { tracerProvider: null, meterProvider: null, loggerProvider: null };
  }

  try {
    // Set up OpenTelemetry diagnostic logger
    const logLevel = process.env.NEXT_PUBLIC_OTEL_LOG_LEVEL || 'info';
    diag.setLogger(
      new DiagConsoleLogger(),
      { logLevel: diagLogLevelFromString(logLevel) }
    );

    // Define resource attributes
    const resourceAttributes = {
      [SEMRESATTRS_SERVICE_NAME]: serviceName,
      [SEMRESATTRS_SERVICE_VERSION]: serviceVersion,
      [SEMRESATTRS_DEPLOYMENT_ENVIRONMENT]: process.env.NODE_ENV || 'production',
      ...SDK_INFO,
    };

    // Detect browser resources and merge with service attributes
    const detectedResources = detectResources({ detectors: [browserDetector] });
    let resource = resourceFromAttributes(resourceAttributes);
    resource = resource.merge(detectedResources);

    // Initialize Traces
    // Use NEXT_PUBLIC_* for client-side environment variables
    const otlpEndpoint =
      config?.tracesEndpoint?.replace('/v1/traces', '') ||
      process.env.NEXT_PUBLIC_OTEL_EXPORTER_OTLP_ENDPOINT ||
      'http://localhost:4318';

    const tracesEndpoint = resolveOtlpSignalUrl(
      config?.tracesEndpoint || process.env.NEXT_PUBLIC_OTEL_EXPORTER_OTLP_TRACES_ENDPOINT,
      otlpEndpoint,
      '/v1/traces'
    );

    const traceExporter = new OTLPTraceExporter({
      url: tracesEndpoint,
    });

    // Configure batch span processor settings from environment variables
    const scheduledDelayMillis = parseInt(
      process.env.NEXT_PUBLIC_OTEL_BSP_SCHEDULE_DELAY || '30000',
      10
    );
    
    const maxExportBatchSize = parseInt(
      process.env.NEXT_PUBLIC_OTEL_BSP_MAX_EXPORT_BATCH_SIZE || '1024',
      10
    );
    
    const exportTimeoutMillis = parseInt(
      process.env.NEXT_PUBLIC_OTEL_BSP_EXPORT_TIMEOUT || '30000',
      10
    );

    const spanProcessor = new BatchSpanProcessor(traceExporter, {
      scheduledDelayMillis,
      maxExportBatchSize,
      exportTimeoutMillis,
    });

    tracerProvider = new WebTracerProvider({
      resource,
      spanProcessors: [new HttpSpanNameProcessor(), spanProcessor],
    });

    // Register with ZoneContextManager for proper async context management (required for RUM)
    tracerProvider.register({ contextManager: new ZoneContextManager() });

    // Initialize Metrics
    const metricsEndpoint = resolveOtlpSignalUrl(
      config?.metricsEndpoint || process.env.NEXT_PUBLIC_OTEL_EXPORTER_OTLP_METRICS_ENDPOINT,
      otlpEndpoint,
      '/v1/metrics'
    );
    const metricExporter = new OTLPMetricExporter({
      url: metricsEndpoint,
    });

    // Configure periodic metric reader settings from environment variables
    const metricExportIntervalMillis = parseInt(
      process.env.NEXT_PUBLIC_OTEL_METRIC_EXPORT_INTERVAL || '60000',
      10
    );

    const metricReader = new PeriodicExportingMetricReader({
      exporter: metricExporter,
      exportIntervalMillis: metricExportIntervalMillis,
    });

    meterProvider = new MeterProvider({
      resource,
      readers: [metricReader],
    });

    // Set global meter provider
    metrics.setGlobalMeterProvider(meterProvider);

    // Initialize Logs (optional but recommended for RUM)
    const logsEndpoint = resolveOtlpSignalUrl(
      config?.logsEndpoint || process.env.NEXT_PUBLIC_OTEL_EXPORTER_OTLP_LOGS_ENDPOINT,
      otlpEndpoint,
      '/v1/logs'
    );
    const logExporter = new OTLPLogExporter({
      url: logsEndpoint,
    });

    loggerProvider = new LoggerProvider({
      resource,
      processors: [new BatchLogRecordProcessor(logExporter)],
    });

    // Set global logger provider
    logs.setGlobalLoggerProvider(loggerProvider);

    // Configure context propagation
    // Use W3C Trace Context propagator for proper trace propagation to API
    propagation.setGlobalPropagator(
      new CompositePropagator({
        propagators: [new W3CTraceContextPropagator()],
      })
    );

    // Build CORS URL list for trace propagation
    const corsUrls: (string | RegExp)[] = [
      /https?:\/\/localhost:8000/,  // API
      /https?:\/\/.*\.snap\.land/,  // Production domains
    ];

    // Add API base URL if set (use NEXT_PUBLIC_* for client-side access)
    const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || process.env.API_BASE_URL;
    if (apiBaseUrl) {
      try {
        const url = new URL(apiBaseUrl);
        corsUrls.push(new RegExp(`https?://${url.host}`));
      } catch (e) {
        // Invalid URL, skip
      }
    }

    if (typeof window !== 'undefined') {
      try {
        const url = new URL(window.location.origin);
        corsUrls.push(new RegExp(`https?://${url.host}`));
      } catch {
        // Invalid URL, skip
      }
    }

    // Register automatic instrumentations (Elastic RUM pattern)
    // getWebAutoInstrumentations includes:
    // - Document load instrumentation
    // - Fetch instrumentation
    // - XMLHttpRequest instrumentation
    // - User interaction instrumentation
    registerInstrumentations({
      instrumentations: [
        ...getWebAutoInstrumentations({
          '@opentelemetry/instrumentation-document-load': {
            enabled: false,
          },
          // Configure fetch instrumentation to propagate trace context
          '@opentelemetry/instrumentation-fetch': {
            propagateTraceHeaderCorsUrls: corsUrls,
            clearTimingResources: true,
            ignoreUrls: ignoredRequestUrls,
            applyCustomAttributesOnSpan: (span, request) => {
              const name = normalizeHttpSpanName(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (request as any)?.method,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (request as any)?.url
              );
              const fallbackName = name ?? normalizeHttpSpanNameFromSpan(span);
              if (fallbackName) {
                span.updateName(fallbackName);
                const path = fallbackName.split(' ').slice(1).join(' ');
                span.setAttribute('http.route', path);
              }
            },
          },
          // Configure XMLHttpRequest instrumentation to propagate trace context
          '@opentelemetry/instrumentation-xml-http-request': {
            propagateTraceHeaderCorsUrls: corsUrls,
            ignoreUrls: ignoredRequestUrls,
            applyCustomAttributesOnSpan: (span, xhr) => {
              const name = normalizeHttpSpanName(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (xhr as any)?.method,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (xhr as any)?.url ?? (xhr as any)?.responseURL
              );
              const fallbackName = name ?? normalizeHttpSpanNameFromSpan(span);
              if (fallbackName) {
                span.updateName(fallbackName);
                const path = fallbackName.split(' ').slice(1).join(' ');
                span.setAttribute('http.route', path);
              }
            },
          },
          // Disable user-interaction spans (button clicks, etc.)
          '@opentelemetry/instrumentation-user-interaction': {
            enabled: false,
          },
        }),
        // Long task instrumentation for detecting UI performance issues
        new LongTaskInstrumentation(),
      ],
    });

    isInitialized = true;

    // Log initialization details to console for debugging
    console.log('[OpenTelemetry RUM] Initialized successfully', {
      serviceName,
      serviceVersion,
      tracesEndpoint,
      metricsEndpoint,
      logsEndpoint,
      tracePropagationUrls: corsUrls.map(u => u.toString()).join(', '),
    });

    diag.info('OpenTelemetry RUM initialized', {
      serviceName,
      serviceVersion,
      tracesEndpoint,
      metricsEndpoint,
      logsEndpoint,
      tracePropagationUrls: corsUrls.map(u => u.toString()).join(', '),
    });

    return { tracerProvider, meterProvider, loggerProvider };
  } catch (error) {
    console.error('[OpenTelemetry RUM] Failed to initialize:', error);
    if (error instanceof Error) {
      console.error('[OpenTelemetry RUM] Error details:', {
        message: error.message,
        stack: error.stack,
      });
    }
    return { tracerProvider: null, meterProvider: null, loggerProvider: null };
  }
}

/**
 * Initialize OpenTelemetry tracing for the browser.
 * 
 * @deprecated Use initializeOpenTelemetry instead for both traces and metrics
 * 
 * @param serviceName - Name of the service (default: 'ui')
 * @param serviceVersion - Version of the service (default: '1.0.0')
 * @param otlpEndpoint - OTLP HTTP endpoint URL (default: from env or localhost)
 */
export function initializeTracing(
  serviceName: string = 'ui',
  serviceVersion: string = '1.0.0',
  otlpEndpoint?: string
): WebTracerProvider | null {
  const { tracerProvider } = initializeOpenTelemetry(serviceName, serviceVersion, {
    tracesEndpoint: otlpEndpoint,
  });
  return tracerProvider;
}

/**
 * Get the current tracer provider instance.
 */
export function getTracerProvider(): WebTracerProvider | null {
  return tracerProvider;
}

/**
 * Get the current meter provider instance.
 */
export function getMeterProvider(): MeterProvider | null {
  return meterProvider;
}

/**
 * Get the current logger provider instance.
 */
export function getLoggerProvider(): LoggerProvider | null {
  return loggerProvider;
}

/**
 * Get a logger instance for manual log emission.
 * 
 * Example usage:
 * ```typescript
 * const logger = getLogger('app-logger');
 * logger.emit({
 *   severityNumber: SeverityNumber.INFO,
 *   body: 'User performed action',
 *   attributes: { 'action.type': 'property_search', 'user.id': userId }
 * });
 * ```
 */
export function getLogger(name: string = 'app-logger') {
  return logs.getLogger(name);
}

/**
 * Create a route-change transaction for Next.js SPA navigation.
 * This should be called when the route changes in a Next.js app.
 * 
 * For Kibana UX view, we need transactions with type "route-change" or "page-load".
 * This function creates a transaction that will be recognized by Kibana's UX dashboard.
 */
export function createRouteChangeTransaction(route: string) {
  if (!tracerProvider) {
    console.warn('[OpenTelemetry RUM] Tracer provider not initialized, cannot create route-change transaction');
    return;
  }

  try {
    const tracer = tracerProvider.getTracer('ui-router');
    
    // Create a transaction span with proper attributes for Kibana UX view
    // The transaction type and name are critical for Kibana to recognize this as a page view
    const span = tracer.startSpan(route || '/', {
      kind: 1, // SpanKind.SERVER (represents a server-side operation)
      attributes: {
        // These attributes are used by Elastic APM to categorize the transaction
        'transaction.type': 'route-change',
        'transaction.name': route || '/',
        'http.route': route || '/',
        'page.route': route || '/',
        // Add semantic conventions for better categorization
        'semantic.conventions.http.route': route || '/',
      },
    });

    // Set the span as a transaction by adding transaction-specific attributes
    // This helps Elastic APM recognize it as a transaction rather than just a span
    span.setAttribute('transaction.type', 'route-change');
    span.setAttribute('transaction.name', route || '/');
    
    // End the span immediately - route changes are typically quick
    // The duration will be very short, but that's expected for route changes
    span.end();

    console.log(`[OpenTelemetry RUM] Created route-change transaction for: ${route || '/'}`);
  } catch (error) {
    console.error('[OpenTelemetry RUM] Failed to create route-change transaction:', error);
  }
}

